import {referralPickerModel} from "./referralOffer";
import type {ReferralOfferModel, ReferralOfferStorefront, ReferralOfferVariant} from "./referralOffer";

/**
 * The gift a visitor meets on the referral LINK itself, before any account exists
 * (kitecorp/transferify-ui#2020, owner 2026-09-05: "when someone accesses a link with a referral they
 * should see the dialog with the offer and claim it on the spot").
 *
 * <b>Why the signed-in model cannot serve this.</b> {@link ./referralOffer.referralOfferModel} builds
 * the picker from `user.referralOffer`, the `referralTrialOffer` group of `GetAccount`. An anonymous
 * reader has no account to read it from, so that model is null for everyone this file is about and the
 * picker never opened for the one person a share link is aimed at.
 *
 * <b>The amounts come from the build-baked public catalogue</b>, the same source the download page's
 * invite panel and the pricing page's footnote already state the reward from. The api publishes a tier
 * there only where an advertised amount AND the Stripe Price that charges it both exist, so a figure
 * this module states is one the checkout can honour.
 *
 * <b>What it deliberately does NOT do is check the code.</b> There is no public endpoint that would
 * answer whether a code is live, and inventing one would let anyone enumerate other people's codes.
 * The refusal is handled after signup instead, where the api already owns it: see
 * {@link referralLandingStage}'s `refused` state.
 *
 * React-free by the repo's logic-outside-react mandate, like every other rule in this directory.
 */

/**
 * The picker an anonymous visitor is shown, or `null` when this storefront cannot state it.
 *
 * The SAME picker the signed-in reader is shown, asked without an account: `referralPickerModel` owns
 * every figure and every row, so the tiers, their order and their amounts cannot differ between the two
 * moments of one journey (kitecorp/transferify-ui#2046). It answers null on the same three storefronts
 * that model does, which are a storefront pricing no tier, one stating no window, and a rung published
 * in another currency than the cards.
 *
 * `referrerName` is null by necessity: the friend's display name lives on the account the api would
 * only reveal to a signed-in caller, so the picker says "a friend" until there is an account to ask
 * with. It is the same fallback the signed-in picker already renders for a referrer with no name.
 */
export function anonymousReferralOfferModel(
	storefront: ReferralOfferStorefront,
): ReferralOfferModel | null {
	return referralPickerModel(storefront, null);
}

/**
 * How the app's attribution submission for the signed-in account has settled, as
 * {@link ./useReferralAttribution} reports it.
 *
 * `unsettled` is not a refusal: the request never got a verdict out of the server, the stored code
 * survives, and the next visit tries again. It is named apart from `refused` because the two owe the
 * reader opposite things: one is "this gift is not for this account", the other is "we have not
 * asked yet".
 */
export type ReferralAttributionStatus = "idle" | "submitting" | "attributed" | "refused" | "unsettled";

/** Everything the landing rule reads, so it can be asked without a browser or a dialog. */
export interface ReferralLandingInput {
	/** The code THIS visit's URL carried, or null. Not the stored first-touch code; see below. */
	arrivingCode: string | null;
	signedIn: boolean;
	/** This browser closed the picker for {@link arrivingCode} (see `./referralOfferDismissal`). */
	dismissed: boolean;
	/** The tier a previous step remembered for this browser, or null. */
	intent: ReferralOfferVariant | null;
	attribution: ReferralAttributionStatus;
	/** The picker for a reader with no account (see {@link anonymousReferralOfferModel}). */
	anonymousModel: ReferralOfferModel | null;
	/** The picker for the signed-in account, from `/account` (see `referralOfferModel`). */
	accountModel: ReferralOfferModel | null;
	/**
	 * The offers yield to the welcome price (`pricing/welcomePrice.offersYieldToWelcome`): a welcome run
	 * is on, sold out or not, OR the read of it has not answered yet (bounded by `WELCOME_READ_TIMEOUT_MS`).
	 * While a run is on the welcome price is the only self-serve offer and the api refuses the gift's intros at the till (kitecorp/transferify#536).
	 */
	welcomeOn: boolean;
}

/** Which of the landing's three surfaces is due, or `idle` for the overwhelming majority of visits. */
export type ReferralLandingStage = "idle" | "offering" | "claiming" | "refused";

/**
 * What the landing owes this reader right now.
 *
 * <b>The refusal outranks the claim</b>, because they are answers to the same question and only one of
 * them is true: a picker opened for an account the api has just refused the gift on would quote an
 * intro price that checkout cannot honour, which is the one outcome this feature must not produce.
 *
 * <b>The offer keys on the ARRIVING code, not on the stored one.</b> `referralAttribution` keeps a
 * first-touch code for thirty days, and keying on that would raise this dialog over every page load
 * for a month. A visitor sees it when they follow the link, and afterwards only if they follow it
 * again.
 *
 * <b>A remembered choice is not a dismissal.</b> A visitor who picked a tier and then closed the
 * sign-in still gets the picker on their next arrival, because every other surface carrying the gift
 * is behind the account they do not have yet.
 *
 * <b>Nothing is claimed until the attribution has been ACCEPTED.</b> The api refuses attribution for
 * an account that already has a live subscription (`REFERRAL_TOO_LATE`), so a checkout opened before
 * that verdict could charge an intro price for a gift the account was never entitled to.
 */
export function referralLandingStage(input: ReferralLandingInput): ReferralLandingStage {
	if (input.intent !== null && input.attribution === "refused") {
		return "refused";
	}
	// After the refusal: that notice is about a gift already promised, not an offer, and stays true.
	if (input.welcomeOn) {
		return "idle";
	}
	if (input.signedIn) {
		return input.intent !== null && input.attribution === "attributed" && input.accountModel !== null
			? "claiming"
			: "idle";
	}
	return input.arrivingCode !== null && !input.dismissed && input.anonymousModel !== null
		? "offering"
		: "idle";
}
